import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StateEntityGroupsService } from '../../state-entity-groups.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-external-state-entity-delete',
    templateUrl: './external-state-entity-delete.component.html',
    styleUrls: ['./external-state-entity-delete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExternalStateEntityDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Delete State Entity";
  minimise = false;
  isLoading = false;
  showEntityLoader = false;

  // Data properties
  rowData: any;
  gridData: any[] = [];
  filterParams: any = {};
  baseUrl: string = '';

  // Global services
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  globalService: any;

  constructor(
    private stateEntityService: StateEntityGroupsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseUrl = this.GENERAL_CONFIG.base_url || '';
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const grid = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = grid && Array.isArray(grid) && grid.length > 0 ? _.cloneDeep(grid) : [];
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
    };
    this.cdr.markForCheck();
  }

  deleteEntity() {
    this.isLoading = true;
    
    // Collect all LE_KEYs from gridData
    let le_keys = "";
    this.gridData.forEach((le: any) => {
      le_keys += le.data.LE_KEY + ',';
    });

    // Call delete service
    this.stateEntityService.deleteExternalStateEntity(
      this.baseUrl,
      le_keys,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isLoading = false;

        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this._snackBar.open('State entity deleted successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit refresh event
          this.requestGridUpdate.emit('refresh');
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Entity cannot be deleted', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isLoading = false;
        console.error('Error deleting external state entity:', error);
        this._snackBar.open('Error occurred while deleting. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
